import React, { useMemo, useState } from 'react';
import { movimentoTemPendente, dividaPendenteMovimento } from '../shared/logica/pagamentos';
import { Package, ReceiptText, FileText, CheckCircle, LogOut, Plus, Eye, Sun } from 'lucide-react';

export default function SimpleDashboardView({ 
  company, 
  movements, 
  budgets, 
  tasks, 
  techJobs,
  onViewMovements,
  onViewDebts,
  onViewBudgets,
  onViewTasks,
  onViewACPanel,
  onLogout
}) {
  const [showBudgetModal, setShowBudgetModal] = useState(false);
  // Dívidas: Todos os movimentos com pagamento PENDING
  const debtsData = useMemo(() => {
    const pendingMovements = movements.filter(m => movimentoTemPendente(m));

    const totalDebt = pendingMovements.reduce((acc, m) => acc + dividaPendenteMovimento(m), 0);

    return {
      total: pendingMovements.length,
      totalDebt
    };
  }, [movements]);

  // Orçamentos: Todos os orçamentos + assistências técnicas
  const budgetsData = useMemo(() => {
    const totalBudgets = (budgets || []).length;
    const totalAssistances = (techJobs || []).length;
    return {
      total: totalBudgets + totalAssistances
    };
  }, [budgets, techJobs]);

  // Tarefas: Apenas tarefas de entrega
  const tasksData = useMemo(() => {
    const deliveryTasks = (tasks || []).filter(t => 
      t.name?.toLowerCase().includes('entrega') || 
      t.notes?.toLowerCase().includes('entrega') ||
      t.type === 'DELIVERY'
    );
    
    return {
      total: deliveryTasks.length
    };
  }, [tasks]);

  return (
    <div className="min-h-screen flex flex-col p-6 bg-[#f1f8e9]">
      {/* Logout button at top right */}
      {onLogout && (
        <div className="flex justify-end mb-6">
          <button
            onClick={onLogout}
            className="p-3 bg-white rounded-xl shadow-sm border border-slate-200 hover:bg-slate-50 transition-colors"
            title="Sair"
          >
            <LogOut size={20} className="text-slate-600" />
          </button>
        </div>
      )}
      <div className="flex-1 flex items-center justify-center">
        <div className="w-full max-w-xs grid grid-cols-2 gap-4">
        {/* Movimentos Card */}
        <div 
          onClick={onViewMovements}
          className="bg-white rounded-2xl border-2 border-slate-200 shadow-lg p-6 cursor-pointer hover:shadow-xl hover:border-blue-300 transition-all active:scale-[0.98] flex items-center justify-center"
        >
          <div className="flex flex-col items-center text-center">
            <div className="w-14 h-14 bg-blue-100 rounded-2xl flex items-center justify-center mb-3">
              <Package size={28} className="text-blue-600" />
            </div>
            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Movimentos</p>
          </div>
        </div>

        {/* Dívidas Card */}
        <div 
          onClick={onViewDebts}
          className="bg-white rounded-2xl border-2 border-slate-200 shadow-lg p-6 cursor-pointer hover:shadow-xl hover:border-red-300 transition-all active:scale-[0.98]"
        >
          <div className="flex flex-col items-center text-center">
            <div className="w-14 h-14 bg-red-100 rounded-2xl flex items-center justify-center mb-3">
              <ReceiptText size={28} className="text-red-600" />
            </div>
            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">Dívidas</p>
            <p className="text-xl font-black text-red-600">{debtsData.totalDebt.toFixed(2)}€</p>
            <p className="text-[9px] text-slate-400 mt-1">{debtsData.total} pendentes</p>
          </div>
        </div>

        {/* Orçamento Card */}
        <div 
          onClick={() => setShowBudgetModal(true)}
          className="bg-white rounded-2xl border-2 border-slate-200 shadow-lg p-6 cursor-pointer hover:shadow-xl hover:border-cyan-300 transition-all active:scale-[0.98]"
        >
          <div className="flex flex-col items-center text-center">
            <div className="w-14 h-14 bg-cyan-100 rounded-2xl flex items-center justify-center mb-3">
              <FileText size={28} className="text-cyan-600" />
            </div>
            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">Orçamento</p>
            <p className="text-2xl font-black text-slate-900">{budgetsData.total}</p>
          </div>
        </div>

        {/* Tarefas Card */}
        <div 
          onClick={onViewTasks}
          className="bg-white rounded-2xl border-2 border-slate-200 shadow-lg p-6 cursor-pointer hover:shadow-xl hover:border-green-300 transition-all active:scale-[0.98]"
        >
          <div className="flex flex-col items-center text-center">
            <div className="w-14 h-14 bg-green-100 rounded-2xl flex items-center justify-center mb-3">
              <CheckCircle size={28} className="text-green-600" />
            </div>
            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">Tarefas</p>
            <p className="text-2xl font-black text-slate-900">{tasksData.total}</p>
          </div>
        </div>

        {/* AC/Painel Solar Card */}
        <div 
          onClick={onViewACPanel}
          className="bg-white rounded-2xl border-2 border-slate-200 shadow-lg p-6 cursor-pointer hover:shadow-xl hover:border-orange-300 transition-all active:scale-[0.98]"
        >
          <div className="flex flex-col items-center text-center">
            <div className="w-14 h-14 bg-orange-100 rounded-2xl flex items-center justify-center mb-3">
              <Sun size={28} className="text-orange-600" />
            </div>
            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">AC/ Painel Solar</p>
          </div>
        </div>
        </div>
      </div>

      {/* Modal de escolha para Orçamentos */}
      {showBudgetModal && (
        <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-md z-[120] flex items-center justify-center p-4 sm:p-6">
          <div className="bg-white rounded-3xl sm:rounded-[2.5rem] p-6 sm:p-10 w-full max-w-sm shadow-2xl space-y-4 sm:space-y-5 text-center">
            <h2 className="text-lg sm:text-xl font-black text-slate-900">Orçamentos</h2>
            <button
              onClick={() => {
                setShowBudgetModal(false);
                onViewBudgets('CREATE');
              }}
              className="w-full py-3 sm:py-4 bg-green-800 text-white font-black rounded-xl sm:rounded-2xl shadow-xl flex items-center justify-center gap-2 hover:bg-green-900 transition-all active:scale-[0.98] text-sm sm:text-base"
            >
              <Plus size={18} className="sm:w-5 sm:h-5" /> Criar
            </button>
            <button
              onClick={() => {
                setShowBudgetModal(false);
                onViewBudgets('VIEW');
              }}
              className="w-full py-3 sm:py-4 border-2 border-green-800 text-green-900 font-black rounded-xl sm:rounded-2xl flex items-center justify-center gap-2 hover:bg-green-50 transition-all active:scale-[0.98] text-sm sm:text-base"
            >
              <Eye size={18} className="sm:w-5 sm:h-5" /> Ver
            </button>
            <button 
              onClick={() => setShowBudgetModal(false)} 
              className="w-full py-2 text-slate-400 font-black hover:text-slate-600 transition-colors text-sm sm:text-base"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

